import { useCallback } from "react";
import PluginSlotRenderer from "./SlotRenderer";
import { runPipeline, anyHookTrue, runHook } from "@markedesk/plugin-sdk/ui";

/**
 * PluginBridge — ponto ÚNICO de integração de plugins na UI.
 *
 * Reúne tudo que o app precisa de plugins:
 *  - <PluginBridge slot=... />        renderiza as ações declaradas no slot
 *  - usePluginBridge(event).run(...)  intercepta uma ação (pipeline before/on/after)
 *  - usePluginBridge().shouldHide(...) consulta hooks de decisão
 *  - PluginHtmlModalHost              host dos modais HTML (montar 1x no layout)
 *
 * Os componentes do app devem usar SEMPRE este módulo (não o PluginSlot direto).
 * O PluginSlot é o motor de render interno.
 */
export default function PluginBridge({ slot, context = {}, as = "buttons", onInsertText, onPluginMessage, children }) {
  if (!slot) return children ?? null;
  return (
    <>
      {/* Conteúdo nativo do app para este slot (ex: um botão da navbar). */}
      {children}
      {/* Ações de plugin declaradas para o mesmo slot. */}
      <PluginSlotRenderer
        slot={slot}
        context={context}
        as={as}
        onInsertText={onInsertText}
        onPluginMessage={onPluginMessage}
      />
    </>
  );
}

/**
 * Hook de integração de plugins.
 *
 * @param {string} event  evento para interceptação (ex: "create-ticket")
 * @returns {{ run, shouldHide, query }}
 *   run(ctx, coreAction)      → pipeline before → on(coreAction) → after.
 *                               Retorna o resultado, ou undefined se cancelado.
 *   shouldHide(hookName, ctx) → true se algum plugin pedir para esconder algo.
 *   query(hookName, ctx)      → resultados crus dos hooks de decisão.
 */
export function usePluginBridge(event) {
  const run = useCallback(
    (ctx, coreAction) => runPipeline(event, ctx || {}, coreAction),
    [event]
  );
  const shouldHide = useCallback((hookName, ctx) => anyHookTrue(hookName, ctx), []);
  const query = useCallback((hookName, ctx) => runHook(hookName, ctx), []);
  return { run, shouldHide, query };
}

export { default as PluginHtmlModalHost } from "./PluginHtmlModalHost";
